import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Box, Button, Checkbox, Input, Radio, Typography } from '@mui/material'
import useTranslations from '../../../../i18n/useTranslations'
import Loader from '../../../views/Loader'
import CompleteInfoItem from '../../../components/listItem/CompleteInfoItem'
import { ReactComponent as MapPin } from '../../../../assets/icons/map-pin.svg'
import { ReactComponent as ArrowRight } from '../../../../assets/icons/arrow-right.svg'
import useStyles from './styles'
import { SHIPPING_STATUS_CODES } from '../../../../common/constants'
import { useState } from 'react'
import useEnhancedFormik from '../../../../hooks/useEnhancedFormik'
import { useFullTracking } from '../../../../services/shippingsTracking'
import TooltipButton from '../../../components/tooltipButton'

const EmailSchema = Yup.object().shape({
  checkbox: Yup.boolean().required(),
  email: Yup.string().email().when('checkbox', {
    is: true,
    then: Yup.string().email().required(),
    otherwise: Yup.string().email(),
  }),
  phone: Yup.string()
    .required()
    .matches(/^[0-9+]*$/g, {
      message: {
        key: 'shipping-send-form:new-address:phone-format-error',
      },
    }),
})

const ActivateReturn = ({
  returnName,
  returnAddress,
  returnShipping,
  isLoading,
  onClickClose,
  onClickConfirm,
}) => {
  const t = useTranslations()
  const classes = useStyles()
  const {
    data: fullTrackingData,
    isLoading: isLoadingFullTracking,
    isSuccess: isFullTrackingDataAvailable,
  } = useFullTracking(returnShipping?.shippingCode)
  const formik = useFormik({
    validationSchema: EmailSchema,
    initialValues: {
      checkbox: false,
      email: returnShipping?.recipentEmail,
      phone: returnShipping?.recipentPhone?.length
        ? returnShipping?.recipentPhone[0]
        : '',
    },
    validateOnMount: true,
  })

  const enhancedFormik = useEnhancedFormik(PropTypes.string.isRequired, formik)

  const renderReturn = () => {
    return (
      <>
        <Typography
          variant="h2"
          className={clsx('secondaryTitle', 'decorator', classes.title)}
        >
          {t('returns:activate:main-title')}
          <br />
          {t('returns:activate:main-subtitle', {
            shippingCode: returnShipping.shippingCode,
          })}
        </Typography>

        <Typography variant="body2" className={classes.mainInfo}>
          {t('returns:activate:main-info')}
        </Typography>

        <CompleteInfoItem
          icon={<MapPin />}
          title={returnName}
          subtitle={returnAddress}
        />

        <Box className={classes.buttonsWrapper}>
          <Button
            size="small"
            disabled={isLoading}
            variant="outlined"
            onClick={onClickClose}
          >
            {t('action:cancel')}
          </Button>

          <Button
            size="small"
            disabled={isLoading}
            endIcon={<ArrowRight />}
            onClick={() =>
              onClickConfirm(fullTrackingData?.slice(-1)[0]?.shippingStatusCode)
            }
          >
            {t('return:activate:action:confirm')}
          </Button>
        </Box>
      </>
    )
  }
  const renderReturnDelivered = () => {
    return (
      <>
        <Typography
          variant="h2"
          className={clsx('secondaryTitle', 'decorator', classes.title)}
        >
          {t('returns:activate:main-title')}
          <br />
          {t('returns:activate:main-subtitle', {
            shippingCode: returnShipping.shippingCode,
          })}
        </Typography>

        <Typography variant="body2" className={classes.mainInfo}>
          {t('returns:activate:main-info')}
        </Typography>

        <Box className={classes.mainInfo}>
          <CompleteInfoItem icon={<MapPin />} subtitle={returnAddress} />
        </Box>

        <Typography variant="body" fontWeight="bold">
          {t('returns:activate:confirm-contact-data')}
        </Typography>

        <Typography variant="body2" className={classes.email}>
          {t('noun:email')}
        </Typography>

        <Input
          className={classes.emailInput}
          name="email"
          value={formik.values.email}
          onChange={enhancedFormik.handleChange}
          error={enhancedFormik?.fieldErrors?.email}
        />

        <Typography variant="body2" className={classes.email}>
          {t('noun:phone')}
        </Typography>

        <Input
          className={classes.emailInput}
          name="phone"
          value={formik.values.phone}
          onChange={enhancedFormik.handleChange}
          error={enhancedFormik?.fieldErrors?.phone}
        />
        {/* ACTIVAR CUANDO DEVOLUCION ENTREGADOS
        <Box className={classes.textWrapper}>
          <Typography variant="body" fontWeight="bold">
            {t('returns:activate:activate-return')}
            <TooltipButton
              type="primary"
              title={t('tooltip:return-email')}
              placement="right"
            />
          </Typography>
        </Box>

        <Box className={classes.checkboxRow}>
          <Checkbox
            className={classes.checkbox}
            name="checkbox"
            checked={formik.values.checkbox}
            onChange={enhancedFormik.handleChange}
            error={enhancedFormik?.fieldErrors?.checkbox}
          />
          <Typography variant="body2">
            {t('returns:activate:confirm-activate-return')}
          </Typography>
        </Box>
        */}

        <Box className={classes.rememberText}>
          <Typography variant="body2">
            {t('returns:activate:remember-text')}
          </Typography>
        </Box>

        <Box className={classes.buttonsWrapper}>
          <Button
            size="small"
            disabled={isLoading}
            variant="outlined"
            onClick={onClickClose}
          >
            {t('action:cancel')}
          </Button>

          <Button
            size="small"
            disabled={
              enhancedFormik?.fieldErrors?.email ||
              enhancedFormik?.fieldErrors?.phone ||
              isLoading
            }
            endIcon={<ArrowRight />}
            onClick={() =>
              onClickConfirm(
                fullTrackingData?.slice(-1)[0]?.shippingStatusCode,
                formik.values.phone,
                formik.values.email,
              )
            }
          >
            {t('return:activate:action:confirm')}
          </Button>
        </Box>
      </>
    )
  }

  return isLoadingFullTracking ? (
    <Loader />
  ) : SHIPPING_STATUS_CODES.delivered ===
    fullTrackingData?.slice(-1)[0]?.shippingStatusCode ? (
    renderReturnDelivered()
  ) : (
    renderReturn()
  )
}

ActivateReturn.propTypes = {
  returnName: PropTypes.string,
  returnAddress: PropTypes.string,
  returnShipping: PropTypes.object,
  isLoading: PropTypes.bool,
  onClickClose: PropTypes.func,
  onClickConfirm: PropTypes.func,
}

export default ActivateReturn
